import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import {
  isAllowedEnergyBusinessVertical,
  isAllowedInsuranceBusinessVertical,
  isAllowedTelecomBusinessVertical,
} from '@/containers/Login/AuthSelectors'

interface UseBusinessTypeReturn {
  type: string
  typeTranslated: string
  getApplicationUrl: (page: string) => string
  getApplicationMenuRoutes: (applicationType: string) => Record<string, string>
  getModulesStatuses: (applicationType: string) => Record<string, any>
  getApplicationTypeFolder: () => string
  translateBusinessTypeToUrl: (businessType: string) => string
  isAllowedEnergyVertical: any
  isAllowedTelecomVertical: any
  isAllowedInsuranceVertical: any
  availableVerticals: string[]
  defaultVertical: string
}

interface UseBusinessTypeParams {
  loaderRoutesEnergy?: Record<string, string>
  loaderRoutesInsurance?: Record<string, string>
  loaderRoutes?: Record<string, string>
  modulesStatuses?: Record<string, any>
  modulesStatusesEnergy?: Record<string, any>
  modulesStatusesInsurance?: Record<string, any>
}

const useBusinessType = ({
  loaderRoutesEnergy = {},
  loaderRoutesInsurance = {},
  loaderRoutes = {},
  modulesStatuses = {},
  modulesStatusesEnergy = {},
  modulesStatusesInsurance = {},
}: UseBusinessTypeParams): UseBusinessTypeReturn => {
  const { type = '' } = useParams()
  const isAllowedEnergyVertical = useSelector(isAllowedEnergyBusinessVertical)
  const isAllowedTelecomVertical = useSelector(isAllowedTelecomBusinessVertical)
  const isAllowedInsuranceVertical = useSelector(isAllowedInsuranceBusinessVertical)

  const availableVerticals: string[] = [
    isAllowedEnergyVertical && 'energie',
    isAllowedTelecomVertical && 'telecom',
    isAllowedInsuranceVertical && 'assurance',
  ].filter(Boolean) as string[]

  const defaultVertical = availableVerticals[0] ?? 'energie'
  const resolveVertical = (vertical: string): string =>
    availableVerticals.includes(vertical) ? vertical : defaultVertical

  const getApplicationTypeFolder = (): string => {
    const safeType = resolveVertical(type)
    if (safeType === 'energie') {
      return 'Energy'
    }
    if (safeType === 'assurance') {
      return 'Insurance'
    }
    return 'Telecom'
  }
  const getApplicationRoutes = (): Record<string, string> => {
    if (type === 'energie') {
      return loaderRoutesEnergy ?? {}
    }
    if (type === 'assurance') {
      return loaderRoutesInsurance ?? {}
    }
    return loaderRoutes ?? {}
  }
  const getApplicationUrl = (page: string): string => {
    const applicationPaths = getApplicationRoutes()
    return applicationPaths[page] ?? ''
  }
  const getModulesStatuses = (applicationType: string): Record<string, any> => {
    const safeType = resolveVertical(applicationType)
    if (safeType === 'energie') {
      return modulesStatusesEnergy
    }
    if (safeType === 'assurance') {
      return modulesStatusesInsurance
    }
    return modulesStatuses
  }

  const getApplicationMenuRoutes = (applicationType: string): Record<string, string> => {
    applicationType = resolveVertical(applicationType)
    if (applicationType === 'energie') {
      return {
        breaks: '/gestione-pause',
        home: '/energie',
        overview: '/energie/contracts',
        clientCallbacks: '/callbacks',
        form: '/energie/subscription/add',
        formV2: '/contact/search',
        users: '/users-mngt',
        teams: '/teams-mngt',
        suppliers: '/suppliers-mngt',
        websalesUsers: '/websales-users-mngt',
        ipAddresses: '/ip-addresses-management',
        offices: '/energie/offices',
        providers: '/energie/organisations/provider',
        servicesStatus: '/energie/services-status',
        devTools: '/dev-tools-v2',
        assistanceProducts: '/energie/additional-sales/assistance',
        monConseillerPersoProducts: '/energie/additional-sales/mon-conseiller-perso',
        carbonOffset: '/energie/additional-sales/carbon-offset',
        additionalSales: '/additional-sales',
        logout: '/logout',
      }
    }
    if (applicationType === 'assurance') {
      return {
        breaks: '/gestione-pause',
        home: '/assurance',
        overview: '/assurance/contracts',
        clientCallbacks: '/callbacks',
        form: '/assurance/subscription/add',
        formV2: '/contact/search',
        users: '/users-mngt',
        teams: '/teams-mngt',
        websalesUsers: '/websales-users-mngt',
        ipAddresses: '/ip-addresses-management',
        providers: '/assurance/organisations/provider',
        servicesStatus: '/assurance/services-status',
        devTools: '/dev-tools-v2',
        assistanceProducts: '/assurance/additional-sales/assistance',
        monConseillerPersoProducts: '/assurance/additional-sales/mon-conseiller-perso',
        carbonOffset: '/assurance/additional-sales/carbon-offset',
        additionalSales: '/additional-sales',
        logout: '/logout',
      }
    }
    return {
      breaks: '/gestione-pause',
      home: '/telecom',
      overview: '/telecom/contracts',
      clientCallbacks: '/callbacks',
      form: '/telecom/subscription/add',
      formV2: '/contact/search',
      users: '/users-mngt',
      teams: '/teams-mngt',
      suppliers: '/suppliers-mngt',
      websalesUsers: '/websales-users-mngt',
      ipAddresses: '/ip-addresses-management',
      providers: '/telecom/organisations/provider',
      providerOptions: '/telecom/provider-options',
      servicesStatus: '/telecom/services-status',
      devTools: '/dev-tools-v2',
      assistanceProducts: '/telecom/additional-sales/assistance',
      monConseillerPersoProducts: '/telecom/additional-sales/mon-conseiller-perso',
      carbonOffset: '/telecom/additional-sales/carbon-offset',
      additionalSales: '/additional-sales',
      logout: '/logout',
    }
  }

  const translateBusinessTypeToUrl = (businessType: string): string => {
    if (businessType === 'energy') {
      return 'energie'
    }
    if (businessType === 'insurance') {
      return 'assurance'
    }
    return 'telecom'
  }

  useEffect(() => {
    if (!type) {
      document.title = 'HOP'
      return
    }

    document.title = 'HOP ' + type.toUpperCase()
  }, [])

  return {
    type,
    typeTranslated: getApplicationTypeFolder(),
    getApplicationUrl,
    getApplicationMenuRoutes,
    getModulesStatuses,
    getApplicationTypeFolder,
    translateBusinessTypeToUrl,
    isAllowedEnergyVertical,
    isAllowedTelecomVertical,
    isAllowedInsuranceVertical,
    availableVerticals,
    defaultVertical,
  }
}

export default useBusinessType
